import React, { memo, useEffect, useRef, useState } from "react"
import QrScanner from "qr-scanner"
import PropTypes from "prop-types"
import api from "../../constants/api"
import { makeStyles } from "@material-ui/core/styles"
import CircularProgress from "@material-ui/core/CircularProgress"

const useStyles = makeStyles(() => ({
	videoContainer: { display: "flex", justifyContent: "center", alignItems: "center" },
	circularProgress: { position: "absolute", top: "50%", left: "45%" },
	videoElement: { width: "100%", height: "100%" },
}))

const ReactQrReader = memo(({ onDetected: handleDetected, onClose }) => {
	const styles = useStyles()
	const videoRef = useRef(null)
	const scannerRef = useRef(null)
	const [scannedData, setScannedData] = useState(null)
	const [parsed, setParsed] = useState(false)
	const [loadingSpinner, setLoadingSpinner] = useState(true)
	function parseStringToJSON(input) {
		const keyValuePairs = input.split("&")
		const result = {}

		for (const pair of keyValuePairs) {
			const [key, value] = pair.split(":")
			if (key && value) {
				result[key.toLowerCase().trim().replace(" ", "_")] = value.trim()
			}
		}
		result["replaced_by"] = api.cognitoID // this is the users UUID both in Cognito AND the rest api
		result["scanned_time"] = new Date().toISOString()
		result["schema_version"] = result["version"] || "0"
		delete result["version"]
		return result
	}

	const startCamera = async () => {
		try {
			videoRef.current.srcObject = await navigator.mediaDevices.getUserMedia({
				video: {
					facingMode: "environment",
					width: { ideal: 1920 }, // High width for better resolution
					height: { ideal: 1080 }, // High height for better resolution
					focusMode: "continuous", // Ensure the camera can autofocus
					aspectRatio: { ideal: 1.0 }, // Maintain a 1:1 aspect ratio for better scanning
					frameRate: { ideal: 30 }, // Sufficient frame rate for stable video
				},
			})
		} catch (e) {
			// if the video ref becomes unavailable, the user probably clicked away and don't actually want the camera
			// we'll log the error, but 9/10 times this is because the user clicked away
			console.error(e)
			if (scannerRef.current) {
				// ensure that the scanner is stopped and destroyed, ending the workers
				scannerRef.current.stop()
				scannerRef.current.destroy()
				scannerRef.current = null
			}
			onClose()
			return
		}
		const scanner = new QrScanner(
			videoRef.current,
			(result) => {
				// The result parameter contains the scanned QR code data.
				// Set the scanned data to the state for processing
				setScannedData(result.data)
			},
			{
				highlightScanRegion: true,
				highlightCodeOutline: true,
				calculateScanRegion: (v) => {
					const smallestDimension = Math.min(v.videoWidth, v.videoHeight)

					// Make scan region smaller to match better small qr codes
					const scanRegionSize = Math.round((1 / 4) * smallestDimension)

					let region = {
						x: Math.round((v.videoWidth - scanRegionSize) / 2),
						y: Math.round((v.videoHeight - scanRegionSize) / 2),
						width: scanRegionSize,
						height: scanRegionSize,
					}
					return region
				},
			},
		)

		scannerRef.current = scanner
		await scanner.start()
	}

	useEffect(() => {
		if (scannedData) {
			// Use the scanned text to parse into JSON
			const parsedData = parseStringToJSON(scannedData)
			handleDetected(parsedData)
			setParsed(true)
		}
	}, [handleDetected, scannedData])

	useEffect(() => {
		if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
			startCamera()
				.then(() => {
					setLoadingSpinner(false)
				})
				.catch(console.error)

			return () => {
				// we want to ALWAYS call the parents onClose function, even if the scanner is already closed
				if (scannerRef.current) {
					// ensure that the scanner is stopped and destroyed, ending the workers
					scannerRef.current.stop()
					scannerRef.current.destroy()
					scannerRef.current = null
				}
				onClose()
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [handleDetected, onClose, parsed])

	return (
		<div className={styles.videoContainer}>
			{loadingSpinner && <CircularProgress className={styles.circularProgress} size={50} thickness={1} />}
			<video className={styles.videoElement} ref={videoRef} autoPlay playsInline />
		</div>
	)
})

ReactQrReader.displayName = "ReactQrReader"

ReactQrReader.propTypes = {
	onDetected: PropTypes.func.isRequired,
	onProcessed: PropTypes.func,
	onError: PropTypes.func,
	onClose: PropTypes.func,
}

export default ReactQrReader
